import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { searchCommands, type PaletteConfig, type PaletteItem } from "@/lib/palette-config";
import { apiRequest } from "@/lib/queryClient";

const QUESTION_PREFIXES = /^(how|what|when|who|where|show|list|tell|is|are|do|does|can|count)\b/i;

export function useCommandPalette(config: PaletteConfig, userRole: string) {
  const [, navigate] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PaletteItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isAILoading, setIsAILoading] = useState(false);
  const [aiAnswer, setAIAnswer] = useState<string | null>(null);
  const [aiAction, setAIAction] = useState<{ type: string; path: string; label: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const aiDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Global keyboard listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // ⌘K / Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        const active = document.activeElement;
        if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || (active as HTMLElement).isContentEditable)) {
          // Allow if the palette is already open (re-focus)
          if (!isOpen) return;
        }
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      // "/" to open (when not in input)
      if (e.key === "/" && !isOpen) {
        const active = document.activeElement;
        if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || (active as HTMLElement).isContentEditable)) return;
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      // Escape to close
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        close();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  // Auto-focus input when opening
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    setSelectedIndex(0);
    setAIAnswer(null);
    setAIAction(null);
    setIsLoading(false);
    setIsAILoading(false);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (aiDebounceRef.current) clearTimeout(aiDebounceRef.current);
  }, []);

  const executeItem = useCallback((item: PaletteItem) => {
    close();
    if (item.action) {
      item.action();
    } else if (item.path) {
      navigate(item.path);
    }
  }, [close, navigate]);

  const onQueryChange = useCallback((newQuery: string) => {
    setQuery(newQuery);
    setSelectedIndex(0);

    if (!newQuery.trim()) {
      setResults([]);
      setAIAnswer(null);
      setAIAction(null);
      setIsLoading(false);
      setIsAILoading(false);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      if (aiDebounceRef.current) clearTimeout(aiDebounceRef.current);
      return;
    }

    // Layer 1+2: Instant navigation + action search
    const allCommands = [...config.navigationCommands, ...config.actionShortcuts];
    const instantResults = searchCommands(newQuery, allCommands);
    setResults(instantResults);

    // Layer 3: Debounced server entity search (300ms, 2+ chars)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (newQuery.trim().length >= 2) {
      setIsLoading(true);
      searchDebounceRef.current = setTimeout(async () => {
        try {
          const res = await apiRequest(`/api/search/global?q=${encodeURIComponent(newQuery.trim())}&limit=20`);
          if (res.ok) {
            const data = await res.json();
            const serverItems: PaletteItem[] = (data.results || []).map((r: any) => ({
              id: `entity-${r.type}-${r.id}`,
              label: r.title,
              description: r.subtitle,
              icon: r.icon,
              type: "entity" as const,
              category: r.type.charAt(0).toUpperCase() + r.type.slice(1).replace(/_/g, ' ') + 's',
              path: r.path,
              relevance: r.relevance,
            }));
            // Merge: instant results first, then server results
            setResults(prev => {
              const navResults = searchCommands(newQuery, allCommands);
              return [...navResults, ...serverItems];
            });
          }
        } catch {
          // Silent fail for search
        } finally {
          setIsLoading(false);
        }
      }, 300);
    }

    // Layer 4: Debounced AI answer (500ms, question detected)
    if (aiDebounceRef.current) clearTimeout(aiDebounceRef.current);
    const isQuestion = QUESTION_PREFIXES.test(newQuery.trim()) || newQuery.trim().endsWith("?");
    if (isQuestion && newQuery.trim().length >= 5) {
      setIsAILoading(true);
      setAIAnswer(null);
      setAIAction(null);
      aiDebounceRef.current = setTimeout(async () => {
        try {
          const res = await apiRequest("/api/palette/ask", {
            method: "POST",
            data: { question: newQuery.trim() },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.source !== "error") {
              setAIAnswer(data.answer);
              setAIAction(data.action);
            }
          }
        } catch {
          // Silent fail for AI
        } finally {
          setIsAILoading(false);
        }
      }, 500);
    } else {
      setAIAnswer(null);
      setAIAction(null);
      setIsAILoading(false);
    }
  }, [config]);

  // Keyboard navigation within results
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % Math.max(results.length, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + results.length) % Math.max(results.length, 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[selectedIndex]) {
        executeItem(results[selectedIndex]);
      } else if (aiAction) {
        close();
        navigate(aiAction.path);
      }
    }
  }, [results, selectedIndex, executeItem, aiAction, close, navigate]);

  return {
    isOpen,
    setIsOpen,
    query,
    results,
    selectedIndex,
    setSelectedIndex,
    isLoading,
    isAILoading,
    aiAnswer,
    aiAction,
    inputRef,
    onQueryChange,
    onKeyDown,
    executeItem,
    close,
  };
}
